import React, { useCallback, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { Plus, Search } from 'lucide-react';
import { CandidateProfileModal } from '../CandidateProfileModal';
import { AddToInviteListModal } from '../AddToInviteListModal';
import { BankSearchResult } from '../../../../utils/types/candi_bank_types';
import { UseBankSearchReturn, useBankSearch } from '../../hooks/useBankSearch';
import { bankResultToCandidateType } from '../../utils/bankSearchAdapters';
import { CandidateType } from '../../constants/mockData';
import { BankSearchList } from './BankSearchList';
import { NewSearchModal } from './NewSearchModal';
import { BankSearchProcessing } from './BankSearchProcessing';
import { BankSearchEditor } from './BankSearchEditor';
import { CoverageBanner } from './CoverageBanner';
import { BankSearchResultsTable } from './BankSearchResultsTable';
import { UnverifiedDrawer } from './UnverifiedDrawer';

interface BankSearchTabProps {
  state?: UseBankSearchReturn;
}

export const BankSearchTab: React.FC<BankSearchTabProps> = ({
  state: externalState,
}) => {
  const internalState = useBankSearch();
  const state = externalState || internalState;

  // Profile / invite modals (reusing existing components)
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] =
    useState<CandidateType | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteCandidate, setInviteCandidate] =
    useState<CandidateType | null>(null);

  const handleViewProfile = useCallback((result: BankSearchResult) => {
    setSelectedCandidate(bankResultToCandidateType(result));
    setShowProfileModal(true);
  }, []);

  const handleInvite = useCallback((result: BankSearchResult) => {
    setInviteCandidate(bankResultToCandidateType(result));
    setShowInviteModal(true);
  }, []);

  const handleCloseProfile = useCallback(() => {
    setShowProfileModal(false);
    setSelectedCandidate(null);
  }, []);

  // Determine what to show in the right panel
  const showProcessing =
    state.isProcessing ||
    state.pollerState === 'polling' ||
    state.pollerState === 'slow' ||
    state.pollerState === 'timeout' ||
    (state.activeSearch &&
      state.activeSearch.status === 'Error' &&
      !!state.processingError);

  const showEditor =
    state.activeSearch &&
    state.activeSearch.status === 'Completed' &&
    !state.isProcessing;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'text-emerald-600 bg-emerald-50';
      default:
        return 'text-slate-600 bg-slate-100';
    }
  };

  const getStatusText = (status: string) => status;

  const getMatchColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-emerald-500';
    if (percentage >= 80) return 'bg-cyan-500';
    if (percentage >= 70) return 'bg-blue-500';
    return 'bg-slate-500';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        gap: 2.5,
        minHeight: 600,
        width: '100%',
      }}
    >
      {/* Left panel: saved search list */}
      <Box sx={{ width: { xs: '100%', lg: 288 }, flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
            px: 0.5,
          }}
        >
          <Typography
            variant='caption'
            fontWeight={700}
            color='text.secondary'
            textTransform='uppercase'
            letterSpacing='0.05em'
          >
            Saved Searches
          </Typography>
          <Button
            size='small'
            variant='contained'
            onClick={() => state.setShowCreateModal(true)}
            startIcon={<Plus size={14} />}
            sx={{
              bgcolor: '#6366f1',
              boxShadow: 'none',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.75rem',
              borderRadius: 2,
              '&:hover': { bgcolor: '#4f46e5', boxShadow: 'none' },
            }}
          >
            New Search
          </Button>
        </Box>
        <BankSearchList
          searches={state.searches}
          total={state.searchListTotal}
          page={state.searchListPage}
          loading={state.searchListLoading}
          activeSearchId={state.activeSearch?.id ?? null}
          onSelect={state.selectSearch}
          onDelete={state.handleDelete}
          onPageChange={state.setSearchListPage}
        />
      </Box>

      {/* Right panel: editor + results */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {!state.activeSearch && !state.isProcessing ? (
          <Paper
            variant='outlined'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 10,
              px: 2,
              textAlign: 'center',
              borderRadius: 3,
              borderColor: 'divider',
              bgcolor: '#ffffff',
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2.5,
                bgcolor: 'rgba(99, 102, 241, 0.1)',
                color: '#6366f1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1.5,
              }}
            >
              <Search size={28} />
            </Box>
            <Typography variant='subtitle1' fontWeight={700} color='text.primary' sx={{ mb: 0.5 }}>
              AI Candidate Matcher
            </Typography>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ maxWidth: 380, lineHeight: 1.6, mb: 3 }}
            >
              Select a saved search from the sidebar or click &ldquo;New Search&rdquo; to extract
              requirements from an opening, pasted JD, or file upload.
            </Typography>
            <Button
              variant='contained'
              onClick={() => state.setShowCreateModal(true)}
              startIcon={<Plus size={14} />}
              sx={{
                bgcolor: '#6366f1',
                boxShadow: 'none',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.75rem',
                borderRadius: 2,
                '&:hover': { bgcolor: '#4f46e5', boxShadow: 'none' },
              }}
            >
              Start New Search
            </Button>
          </Paper>
        ) : showProcessing ? (
          <BankSearchProcessing
            pollerState={
              state.pollerState as
                | 'idle'
                | 'polling'
                | 'slow'
                | 'timeout'
                | 'completed'
                | 'error'
            }
            errorMessage={state.processingError}
            onRetry={state.retrySearch}
            searchTitle={state.activeSearch?.title ?? null}
          />
        ) : showEditor && state.activeSearch ? (
          <Stack spacing={2}>
            {/* Compact Profile Editor */}
            <BankSearchEditor
              search={state.activeSearch}
              pool={state.pool}
              isReindexing={state.isReindexing}
              onPatchSkills={state.patchSkills}
              onPatchJobTitle={state.patchJobTitle}
              onPatchEducations={state.patchEducations}
              onPatchCertifications={state.patchCertifications}
              onPatchRemoveMatches={state.patchRemoveMatches}
              onPatchExperience={state.patchExperience}
              onPatchTitle={state.patchTitle}
              onRefreshFromOpening={state.handleRefreshFromOpening}
              weightOverride={state.weightOverride}
              onWeightOverrideChange={state.setWeightOverride}
              onCommitWeights={state.commitWeights}
              onResetWeights={state.resetWeights}
              onApplyWeightOverride={state.applyWeightOverride}
            />

            {/* Compact Coverage Banner */}
            <CoverageBanner
              pool={state.pool}
              skillMatchesStale={state.skillMatchesStale}
            />

            {/* Results Table with NameEmailAvatar */}
            <BankSearchResultsTable
              results={state.results}
              pool={state.pool}
              search={state.activeSearch}
              total={state.resultsTotal}
              page={state.resultsPage}
              limit={20}
              loading={state.resultsLoading}
              onPageChange={state.setResultsPage}
              onViewProfile={handleViewProfile}
              onInvite={handleInvite}
            />

            {/* Unverified Drawer */}
            {state.pool && state.pool.unverified > 0 && (
              <UnverifiedDrawer
                unverifiedCount={state.pool.unverified}
                expanded={state.showUnverified}
                onToggle={() =>
                  state.setShowUnverified(!state.showUnverified)
                }
                unverifiedResults={state.unverifiedResults}
                onViewProfile={handleViewProfile}
              />
            )}
          </Stack>
        ) : null}
      </Box>

      {/* New Search Modal */}
      <NewSearchModal
        isOpen={state.showCreateModal}
        onClose={() => state.setShowCreateModal(false)}
        onCreateFromFile={state.createFromFile}
        onCreateFromText={state.createFromText}
        onCreateFromOpening={state.createFromOpening}
        loading={state.createLoading}
      />

      {/* Reuse existing candidate modals */}
      <CandidateProfileModal
        candidate={selectedCandidate}
        isOpen={showProfileModal}
        onClose={handleCloseProfile}
        getStatusColor={getStatusColor}
        getStatusText={getStatusText}
        getMatchColor={getMatchColor}
        onInvite={() => {
          if (selectedCandidate) {
            setInviteCandidate(selectedCandidate);
            setShowInviteModal(true);
          }
        }}
      />

      <AddToInviteListModal
        isOpen={showInviteModal}
        onClose={() => {
          setShowInviteModal(false);
          setInviteCandidate(null);
        }}
        candidateIds={inviteCandidate ? [inviteCandidate.id] : []}
        candidateName={inviteCandidate?.name}
      />
    </Box>
  );
};
